import { useEffect, useState } from 'react';
import { Modal } from '../components/Modal';
import { useProducts, useAssignmentMutations } from './api';
import { ProductThumb } from '../visitor/ProductThumb';
import { Pagination, Spinner } from '../components/ui';
import { money, weight } from '../lib/format';
import { useToast } from '../components/Toast';
import { ApiRequestError } from '../lib/api';

export function AssignProductsModal({
  open,
  onClose,
  participationId,
  stallId,
  rackId,
  rackLabel,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  participationId: string;
  stallId: string;
  rackId: string;
  rackLabel: string;
  onDone: () => void;
}) {
  const toast = useToast();
  const { assign } = useAssignmentMutations(participationId);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [price, setPrice] = useState('');
  const [qty, setQty] = useState('');
  const [result, setResult] = useState<{ assigned: number; skipped: { reason: string }[] } | null>(null);

  const { data, isFetching } = useProducts({
    page,
    limit: 12,
    status: 'active',
    search: search || undefined,
  });
  const rows = data?.data ?? [];

  useEffect(() => {
    if (open) {
      setPicked(new Set());
      setSearch('');
      setPage(1);
      setPrice('');
      setQty('');
      setResult(null);
    }
  }, [open]);

  const toggle = (id: string) =>
    setPicked((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const submit = async () => {
    if (!picked.size) return;
    try {
      const res = (await assign.mutateAsync({
        stallId,
        rackId,
        productIds: [...picked],
        ...(price ? { displayPrice: price } : {}),
        ...(qty ? { availableQuantity: Number(qty) } : {}),
      })) as { assigned: number; skipped: { reason: string }[] };
      setResult(res);
      onDone();
      if (res.assigned && !res.skipped.length) {
        toast(`Assigned ${res.assigned} product${res.assigned === 1 ? '' : 's'}`, 'success');
        onClose();
      }
    } catch (e) {
      toast(e instanceof ApiRequestError ? e.message : 'Assign failed', 'error');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Assign products → ${rackLabel || 'rack'}`}
      size="lg"
      footer={
        <>
          <button className="btn-ghost" onClick={onClose}>
            Close
          </button>
          <button className="btn-primary" onClick={submit} disabled={assign.isPending || picked.size === 0}>
            {assign.isPending ? <Spinner className="h-4 w-4" /> : `Assign ${picked.size || ''}`}
          </button>
        </>
      }
    >
      {result && (
        <div className="mb-3 rounded-lg border border-ink-100 bg-ink-50 p-3 text-sm">
          Assigned <b>{result.assigned}</b>.
          {result.skipped.length > 0 && (
            <>
              {' '}
              Skipped <b>{result.skipped.length}</b> —{' '}
              {[...new Set(result.skipped.map((s) => s.reason.replace(/_/g, ' ')))].join(', ')}.
            </>
          )}
        </div>
      )}

      <div className="mb-3 grid grid-cols-2 gap-3">
        <label className="block">
          <span className="label">Display price (₹) — optional, blank = product price</span>
          <input className="input" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
        </label>
        <label className="block">
          <span className="label">Available quantity — optional, blank = product base qty</span>
          <input className="input" inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} />
        </label>
      </div>

      <input
        className="input mb-2"
        placeholder="Search barcode / Photo ID / name…"
        value={search}
        onChange={(e) => {
          setPage(1);
          setSearch(e.target.value);
        }}
      />

      <div className="max-h-[340px] divide-y divide-ink-50 overflow-y-auto rounded-lg border border-ink-100">
        {rows.map((p) => {
          const on = picked.has(p._id);
          return (
            <label key={p._id} className={`flex cursor-pointer items-center gap-3 p-2 ${on ? 'bg-gold-50' : 'hover:bg-ink-50'}`}>
              <input type="checkbox" checked={on} onChange={() => toggle(p._id)} />
              <div className="h-9 w-9 overflow-hidden rounded-md bg-ink-50">
                <ProductThumb src={p.primaryImage?.thumbnailUrl ?? p.images?.[0]?.thumbnailUrl} alt={p.barcodeNo} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{p.displayName || p.photoId || p.barcodeNo}</p>
                <p className="font-mono text-xs text-ink-400">
                  #{p.barcodeNo}
                  {p.size ? ` · ${p.size}` : ''} · {weight(p.grossWeight)}
                </p>
              </div>
              <span className="text-sm text-ink-500">{money(p.displayPrice ?? p.sellingPrice)}</span>
            </label>
          );
        })}
        {rows.length === 0 && <p className="p-4 text-center text-sm text-ink-400">No products found.</p>}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-ink-400">
          {picked.size} selected{isFetching ? ' · loading…' : ''}
        </span>
        <Pagination page={page} pages={data?.meta?.pages ?? 1} onPage={setPage} />
      </div>
    </Modal>
  );
}
